import React, { useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";

import UploadBar from "components/atoms/UploadBar/UploadBar";
import { deleteFile } from "api";
import errorToast from "helpers/errorToast";

type InvoiceFormOtherAttachmentListItemProps = {
  currentFile: any;
  onRemove: Function;
};

function InvoiceFormOtherAttachmentListItem({ currentFile, onRemove }: Readonly<InvoiceFormOtherAttachmentListItemProps>) {
  const [isLoading, setIsLoading] = useState(false);

  const handleRemoveClick = async () => {
    const referenceNumber = currentFile.reference_number;
    if (currentFile.progress > 0 && currentFile.abortController) {
      currentFile.abortController.abort();
    } else if (referenceNumber && !currentFile.error) {
      setIsLoading(true);
      try {
        await deleteFile(referenceNumber);
        setIsLoading(false);
      } catch (e) {
        errorToast(e, "Could not delete file, please try again later");
        setIsLoading(false);
        return;
      }
    }
    onRemove(currentFile);
  };

  return (
    <Row className="mb-2">
      <Col>
        <div className="d-flex flex-column">
          {currentFile.url ? (
            <a href={currentFile.url} target="_blank" rel="noopener noreferrer">
              {currentFile.name}
            </a>
          ) : (
            currentFile.name
          )}
          {currentFile?.stop?.sequence !== null &&
            currentFile?.stop?.sequence !== undefined && (
              <span className="text-secondary text-xxs font-weight-normal">
                <FormattedMessage
                  id="billing.invoiceAttachment.stopLabel"
                  defaultMessage="Stop {sequences}"
                  values={{ sequences: currentFile.stop.sequence }}
                />
              </span>
            )}
          {currentFile?.stop?.full_address && (
            <span className="text-secondary text-xxs font-weight-normal">{currentFile.stop.full_address}</span>
          )}
        </div>
      </Col>
      <Col>
        {isLoading ? (
          <div>
            <FontAwesomeIcon icon={faCircleNotch} className="font-16 text-secondary " spin />
          </div>
        ) : (
          <>
            {!!currentFile.progress && !currentFile.error && <UploadBar percentCompleted={currentFile.progress} />}
            {!currentFile.progress && currentFile.error && (
              <div className="text-danger">
                <div>
                  <FormattedMessage
                    id="new-request.attachments.attachmentFile.uploadFailed"
                    defaultMessage="Upload failed. Please try again"
                  />
                </div>
                <div>
                  {currentFile?.errorMessage ? (
                    currentFile.errorMessage
                  ) : (
                    <FormattedMessage
                      id="new-request.attachments.attachmentFile.maxUploadSize"
                      defaultMessage="(max filesize 15mb)"
                    />
                  )}
                </div>
              </div>
            )}
            {!currentFile.progress && !currentFile.error && currentFile.url && (
              <span className="text-nowrap">
                <FormattedMessage id="new-request.otherAttachment.fileUploaded" defaultMessage="File uploaded" />
              </span>
            )}
          </>
        )}
      </Col>
      <Col className="d-flex justify-content-center">
        <Button type="button" variant="link" className="remove-btn p-0" onClick={handleRemoveClick} disabled={isLoading}>
          <FormattedMessage id="general.remove" defaultMessage="Remove" />
        </Button>
      </Col>
    </Row>
  );
}

export default InvoiceFormOtherAttachmentListItem;

InvoiceFormOtherAttachmentListItem.propTypes = {
  currentFile: PropTypes.instanceOf(Object).isRequired,
  onRemove: PropTypes.func.isRequired,
};
